import { GelConfig } from "@/types/gelConfig";
import { GelProximityPickupDetails } from "./elementUtils";
import PickupCarrierManager from "./PickupCarrierManager";

enum SubmitButtonBehavior {
  DEFAULT = "DEFAULT",
  SELECT_PICKUP = "SELECT_PICKUP",
}

class CarrierSelectionHandler {
  private radio: HTMLInputElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private defaultButtonText: string | null = null;
  private defaultButtonIcon: string | null = null;
  private pickupCarrierManager: PickupCarrierManager;
  private pickupIcon = "&#xe558;";

  constructor(pickupCarrierManager: PickupCarrierManager) {
    this.pickupCarrierManager = pickupCarrierManager;
  }

  private isSelectionDetailsEmpty(): boolean {
    const details = document.getElementById(GelProximityPickupDetails);
    return !details || details.innerHTML.trim() === "";
  }

  private setSubmitButtonState(
    button: HTMLButtonElement,
    enabled: boolean,
    behavior: SubmitButtonBehavior,
  ): void {
    button.classList.toggle("disabled", !enabled);
    button.disabled = !enabled;

    const icon = button.querySelector(".material-icons");
    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
    );

    switch (behavior) {
      case SubmitButtonBehavior.SELECT_PICKUP:
        if (!this.defaultButtonText && textNode) {
          this.defaultButtonText = textNode.textContent!.trim();
        }
        if (!this.defaultButtonIcon && icon) {
          this.defaultButtonIcon = icon.innerHTML;
        }

        if (textNode && icon) {
          textNode.textContent =
            window.gelProximityConfig.selectPickupCarrierBtnText;
          icon.innerHTML = this.pickupIcon;
        } else {
          button.innerText =
            window.gelProximityConfig.selectPickupCarrierBtnText;
        }

        this.pickupCarrierManager.addPickupCarrierListener(button);
        break;

      case SubmitButtonBehavior.DEFAULT:
      default:
        if (this.defaultButtonText) {
          if (textNode) {
            textNode.textContent = this.defaultButtonText;
          } else {
            button.innerText = this.defaultButtonText;
          }
        }

        if (icon && this.defaultButtonIcon) {
          icon.innerHTML = this.defaultButtonIcon;
        }

        this.pickupCarrierManager.removePickupCarrierListener(button);
        break;
    }
  }

  private findDeliveryRadio(carrierId: string): HTMLInputElement | null {
    const selectors = [
      `.js-delivery-option input[value="${carrierId}"]`,
      `.js-delivery-option input[value="${carrierId},"]`,
    ];
    for (const selector of selectors) {
      const radio = document.querySelector<HTMLInputElement>(selector);
      if (radio) {
        return radio;
      }
    }
    return null;
  }

  public updateSubmitOrderButtonState(isErrorPresent: boolean = false): void {
    if (!this.radio || !this.submitButton) {
      return;
    }

    if (this.radio.checked) {
      const behavior = this.isSelectionDetailsEmpty()
        ? SubmitButtonBehavior.SELECT_PICKUP
        : SubmitButtonBehavior.DEFAULT;

      this.setSubmitButtonState(this.submitButton, !isErrorPresent, behavior);
    } else {
      this.setSubmitButtonState(
        this.submitButton,
        true,
        SubmitButtonBehavior.DEFAULT,
      );
    }
  }

  public handleCarrierSelection(gelConfig: GelConfig): void {
    this.radio = this.findDeliveryRadio(gelConfig.carrierId);
    if (!this.radio) {
      return;
    }

    const deliveryOption = this.radio.closest(
      ".js-delivery-option",
    ) as HTMLElement;
    if (!deliveryOption) {
      return;
    }

    const form = deliveryOption.closest("form") as HTMLFormElement | null;
    this.submitButton =
      form?.querySelector<HTMLButtonElement>(
        'button[type="submit"], input[type="submit"]',
      ) ?? null;

    if (!form || !this.submitButton) {
      return;
    }

    this.updateSubmitOrderButtonState();

    const allRadios = form.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]',
    );
    allRadios.forEach((r) =>
      r.addEventListener("change", () => this.updateSubmitOrderButtonState()),
    );
  }
}

export default CarrierSelectionHandler;
